import { useCallback, useContext } from 'react'

import { Button, Box, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { lighten } from '@material-ui/core/styles/colorManipulator'

import { CONTENT_TYPES } from '@scoir/college-content'

import ContentModalContext from '../../../ContentModalProvider/Context'
import { FIELD_NAMES, LABELS } from '../../../components/constants'
import { useImageUpload, IimageObject } from './use-image-upload'

const useStyles = makeStyles((theme) => ({
  uploadImageButton: {
    width: '100%',
    position: 'relative',
    background: lighten(theme.palette.info.main, 0.85),
    borderRadius: theme.shape.borderRadius,
    border: `1px dashed ${theme.palette.grey[400]}`,
  },
  uploadFileInput: {
    width: '100%',
    height: '100%',
    opacity: 0,
    top: 0,
    position: 'absolute',
  },
  previewImage: {
    width: '100%',
    height: 'auto',
    position: 'relative',
    top: 0,
    zIndex: -1,
    pointerEvents: 'none',
  },
}))

const ImageUpload = () => {
  const classes = useStyles()

  const { type, item, scid, setImageUploadOpen, onUpdateFields } = useContext(ContentModalContext)
  const isPostContent = type !== CONTENT_TYPES.PROGRAMS && type !== CONTENT_TYPES.SESSIONS

  const onPickImage = useCallback(
    (image: IimageObject) => {
      onUpdateFields({
        [FIELD_NAMES.IMAGE_URL]: image.url,
        [FIELD_NAMES.IMAGE_BUCKET]: image.container,
        [FIELD_NAMES.IMAGE_FILENAME]: image.filename,
        [FIELD_NAMES.IMAGE_MIME]: image.mimetype,
        [FIELD_NAMES.IMAGE_KEY]: image.key?.replace(`/${window.FILE_STACK_GCS_ROOT_PATH}/college-content/${scid}/`, ''),
        [FIELD_NAMES.IMAGE_SIZE]: image.size,
      })
      setImageUploadOpen(false)
    },
    [scid, onUpdateFields, setImageUploadOpen],
  )

  const onImageUpload = useImageUpload({
    scid,
    onPickImage,
    setImageUploadOpen,
  })

  const hasImage = !!item?.ImageUrl
  const buttonText = hasImage ? 'Replace Image' : `Choose Image${isPostContent ? '*' : ''}`

  return (
    <div className={classes.uploadImageButton}>
      <Box
        p={3}
        width="100%"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        position="relative"
        zIndex={1}
      >
        {hasImage ? (
          <img
            src={item.ImageUrl}
            alt={''}
            aria-label="Uploaded Image"
            className={classes.previewImage}
          />
        ) : (
          <>
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
            >
              Drag an image (jpeg, pjpeg, or png) to upload or
            </Typography>
            <br />
          </>
        )}
        <input
          type="file"
          aria-label={LABELS.IMAGE}
          className={classes.uploadFileInput}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onDrop={onImageUpload}
        />
        <Box position={hasImage ? 'absolute' : 'relative'}>
          <Button
            size="large"
            color="primary"
            variant="contained"
            onClick={onImageUpload}
          >
            {buttonText}
          </Button>
        </Box>
      </Box>
    </div>
  )
}

export default ImageUpload
